import { httpClient } from "@/services/http/client";

export type TenantBootstrap = {
  tenant: {
    id: string;
    slug: string;
    name: string;
    logoUrl: string | null;
    branding: {
      accentColor?: string | null;
    };
    /** §31: "open" (anyone can register) or "approved_only" (only listed emails). */
    joinPolicy?: "open" | "approved_only";
    /** Plan 6 S5: this school's terms are still a draft. */
    termsDraft?: boolean;
    /** Plan 6 S5: this school has its AI tutor switched on. */
    aiTutorEnabled?: boolean;
    /** The school's privacy contact, shown on its Privacy notice; null when unset. */
    privacyContactEmail?: string | null;
  };
};

export async function fetchTenantBootstrap(slug: string): Promise<TenantBootstrap> {
  const res = await httpClient.get<{ data: TenantBootstrap }>(`/v1/tenants/${slug}/bootstrap`);
  return res.data.data;
}

/** N16: the answer carries the server's idle limit, which the idle timer then follows. */
export async function postSessionHeartbeat(): Promise<{ idleTimeoutSec?: number } | null> {
  const res = await httpClient.post<{ data?: { idleTimeoutSec?: number } }>(
    "/v1/session/heartbeat",
  );
  return res?.data?.data ?? null;
}

export type CatalogCourse = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  courseType: string;
};

export type TenantCatalog = {
  tenant: { slug: string; name: string };
  courses: CatalogCourse[];
  enrolledCourseIds: string[];
};

export async function fetchTenantCatalog(slug: string): Promise<TenantCatalog> {
  const res = await httpClient.get<{ data: TenantCatalog }>(`/v1/tenants/${slug}/catalog`);
  return res.data.data;
}

export type CourseDetail = {
  tenant: { slug: string; name: string };
  course: {
    id: string;
    slug: string;
    title: string;
    description: string | null;
    thumbnailUrl: string | null;
    courseType: string;
  };
  isWaitlistCourse: boolean;
  prices: Array<{
    id: string;
    currency: string;
    displayAmount: string;
    gateway: "razorpay" | "stripe";
  }>;
  free: boolean;
  /** PAY-1: whether, and in which currency, this tenant can take payment for the course. */
  checkout: {
    available: boolean;
    currency: "INR" | "USD" | null;
    provider: "razorpay" | "stripe" | null;
    currencies: Array<{ currency: "INR" | "USD"; provider: "razorpay" | "stripe" }>;
    reason?: string;
  };
  /** PAY-8: show the coupon box only when a coupon could apply. */
  couponsAvailable: boolean;
  /** PAY-9: the ?ref= code, and whether checkout would credit it. */
  referral: { code: string; accepted: boolean } | null;
  curriculum: Array<{
    id: string;
    title: string;
    lessonCount: number;
    lessons: Array<{ id: string; title: string; subtitle: string | null }>;
  }>;
  sessionCount: number;
  reviewSummary: { count: number; averageRating: number };
  reviews: Array<{
    id: string;
    authorName: string;
    rating: number;
    body: string | null;
    createdAt: string;
  }>;
  enrollmentState: "anonymous" | "not_enrolled" | "enrolled";
  canContinueLearning: boolean;
  canSelfEnroll: boolean;
  canSubmitReview: boolean;
};

export async function fetchCourseDetail(
  tenantSlug: string,
  courseSlug: string,
  referralCode?: string | null,
): Promise<CourseDetail> {
  const query = referralCode ? `?ref=${encodeURIComponent(referralCode)}` : "";
  const res = await httpClient.get<{ data: CourseDetail }>(
    `/v1/tenants/${tenantSlug}/courses/${courseSlug}${query}`,
  );
  return res.data.data;
}
